import {ga4PocFlag} from '@advisor/remote-config';

// NOTE:  This file is in the common folder but is no longer used by the client platform.
//        It is still used by the advisor platform and will remain in the common folder until that
//        is no longer the case.
const trackingId = process.env.VUE_APP_GOOGLE_UA_ID;
const gtmId = process.env.VUE_APP_GOOGLE_GTM_ID;
const gtagId = process.env.VUE_APP_GOOGLE_GTAG_ID;

const ENABLED = process.env.VUE_APP_GOOGLE_ENABLED === 'true' && (trackingId || gtmId);
const VERBOSE = process.env.VUE_APP_GOOGLE_VERBOSE === 'true';

const dataLayerName = `dataLayerGTM`;
const engines = [`GTM`];

// eslint-disable-next-line no-return-await
const ga4Flag = async () => await ga4PocFlag();

const redactionPatterns = [
  /([\d.a-z]+?)(@|(%40))(.+?)\.(\w)+(\W)*/gi,
  /((tel=)|(telephone=)|(phone=)|(mobile=)|(mob=))[\d\s+][^&/?]+/gi,
  /((firstname=)|(lastname=)|(surname=))[^&/?]+/gi,
  /((password=)|(passwd=)|(pass=))[^&/?]+/gi,
  /((postcode=)|(zipcode=)|(zip=))[^&/?]+/gi,
];
const redactionKeys = new Set([
  'password',
  'pass',
  'passwd',
  'postcode',
  'zipcode',
  'zip',
  'email',
  'firstname',
  'lastname',
  'surname',
  'tel',
  'telephone',
  'phone',
  'mobile',
  'mob',
]);
// eslint-disable-next-line @typescript-eslint/naming-convention
const _message = (level, ...args) => {
  if (!VERBOSE) return;

  console[level]('[ANALYTICS]', ...args);
};
// eslint-disable-next-line @typescript-eslint/naming-convention
const _logger = {
  log: (...args) => {
    _message('log', ...args);
  },
  warn: (...args) => {
    _message('warn', ...args);
  },
  error: (...args) => {
    _message('error', ...args);
  },
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const _methods = {
  GTM: {
    bootstrap: () => {
      if (!gtmId) return;

      _logger.log('Bootstrapping for GTM');

      window[dataLayerName] = window[dataLayerName] || [];

      if (ga4Flag()) {
        window[dataLayerName].push({
          gtag: Date.now(),
          event: 'gtag.js',
        });
      } else {
        window[dataLayerName].push({
          'gtm.start': Date.now(),
          event: 'gtm.js',
        });
      }

      const el = document.createElement('script');

      el.setAttribute('async', 'true');
      if (ga4Flag()) {
        el.setAttribute(
          'src',
          `https://www.googletagmanager.com/gtag/js?id=${gtagId}${
            // eslint-disable-next-line no-negated-condition
            dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : ''
          }`
        );
      } else {
        el.setAttribute(
          'src',
          // eslint-disable-next-line no-negated-condition
          `https://www.googletagmanager.com/gtm.js?id=${gtmId}${dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : ''}`
        );
      }

      // awfully brave of google to always assume there will be a script tag
      const firstScript = document.getElementsByTagName('script')[0];

      firstScript.parentNode.insertBefore(el, firstScript);

      const ns = document.createElement('noscript');
      const gtmFrame = document.createElement('iframe');

      gtmFrame.setAttribute('src', `https://www.googletagmanager.com/ns.html?id=${gtmId}`);
      gtmFrame.setAttribute('height', '0');
      gtmFrame.setAttribute('width', '0');
      gtmFrame.setAttribute('style', 'display:none;visibility:hidden');
      ns.append(gtmFrame);
      document.body.insertBefore(ns, document.body.firstElementChild);
    },

    pageView: (to) => {
      if (!gtmId) return;

      window[dataLayerName].push({
        event: 'Pageview',
        page_title: to.name,
        page_path: to.fullPath,
      });

      _logger.log(window[dataLayerName]);
    },
    event: (event, data) => {
      if (!gtmId) return;

      window[dataLayerName].push({
        event,
        ...data,
      });

      _logger.log(window[dataLayerName]);
    },
  },
};

export const bootstrapApp = () => {
  if (!ENABLED) {
    _logger.warn('Bootstrap called, but analytics are not enabled');

    return;
  }

  engines.forEach((e) => _methods[e].bootstrap());
};

const reclaim = (redacted, pattern) => redacted.replace(pattern, '**REDACTED**');

// default to a depth of 5, you can make it a shallow redact with a depth of 1
const redact = (input, depth = 5) => {
  if (!depth || !input) return input;

  if (typeof input === 'string')
    return redactionPatterns.reduce((accumulator, element) => reclaim(accumulator, element), input);

  // TODO: better object checking
  if (Object.prototype.toString.call(input) === '[object Object]') {
    return Object.keys(input).reduce((redacted, key) => {
      if (redactionKeys.has(key.toLowerCase())) {
        // eslint-disable-next-line no-param-reassign
        redacted[key] = '**REDACTED**';
      } else {
        // eslint-disable-next-line no-param-reassign
        redacted[key] = redact(input[key], depth - 1);
      }

      return redacted;
    }, {});
  }

  return input;
};

export const pageView = (_to, _from) => {
  if (!ENABLED) {
    _logger.warn('pageView called, but analytics are not enabled');

    return;
  }

  const [to, from] = [redact(_to), redact(_from)];

  _logger.log('pageView', to, from);
  engines.forEach((e) => _methods[e].pageView(to, from));
};

// eslint-disable-next-line no-shadow
// eslint-disable-next-line @typescript-eslint/no-shadow
export const event = (event, data) => {
  if (!ENABLED) {
    _logger.warn('event, but analytics are not enabled');

    return;
  }

  const redacted = redact(data);

  _logger.log('event', redacted);
  engines.forEach((e) => _methods[e].event(event, redacted));
};
